import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { MetricCard, PageHeader, Section, Pill } from "@/components/ui-kit";
import { Wallet, Banknote } from "lucide-react";
import { useAuth } from "@/lib/auth";

const BANNER = "https://images.unsplash.com/photo-1765648763990-ad0ee9b546c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmaW5hbmNpYWwlMjBwcm9mZXNzaW9uYWwlMjBjYWZlfGVufDB8fHx8MTc3ODE1ODgxMXww&ixlib=rb-4.1.0&q=85";

export default function CustomerPortal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/customer").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return <div className="label">Loading…</div>;
  return (
    <div data-testid="customer-portal">
      <div className="rounded-md overflow-hidden mb-8 relative" style={{ backgroundImage: `linear-gradient(135deg, rgba(44,85,69,0.85), rgba(44,85,69,0.55)), url(${BANNER})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="p-8 text-white">
          <div className="label text-white/70 mb-2">My portal</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Welcome back, {user?.name?.split(" ")[0] || "there"}.</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <MetricCard label="My Savings Balance" value={fmtMoney(data.savings_total)} icon={Wallet} accent="sage" testId="metric-my-savings" sub={`${data.savings_accounts.length} account(s)`} />
        <MetricCard label="My Outstanding Loans" value={fmtMoney(data.outstanding_loans)} icon={Banknote} accent="terracotta" testId="metric-my-loans" sub={`${data.loans.length} loan(s)`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="My Savings Accounts" testId="my-savings">
          {data.savings_accounts.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No accounts yet.</div>
          ) : (
            <ul className="space-y-2">
              {data.savings_accounts.map((a) => (
                <li key={a.id} className="flex justify-between items-center p-3 border border-border rounded-md">
                  <div>
                    <div className="text-xs text-muted-foreground">{a.account_number}</div>
                    <div className="font-medium">Interest: {a.interest_rate}%</div>
                  </div>
                  <div className="metric text-xl">{fmtMoney(a.balance)}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Upcoming Repayments" testId="my-upcoming">
          {data.upcoming_repayments.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No upcoming repayments.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left">
                <th className="label py-2">Due</th><th className="label py-2 text-right">Amount</th><th className="label py-2 text-right">Status</th>
              </tr></thead>
              <tbody>
                {data.upcoming_repayments.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="py-2.5">{fmtDate(r.due_date)}</td>
                    <td className="py-2.5 text-right tabular-nums">{fmtMoney(r.scheduled_amount)}</td>
                    <td className="py-2.5 text-right"><Pill variant={r.status === "OVERDUE" ? "warning" : "info"}>{r.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </div>
  );
}
